import ko from "knockout";
import "flow/bindings/fancytreeBinding";
import { CloseCallback } from "flow/api/types";
import MyFlow from "flow/MyFlow";
import { fancytreeReadonlyDefaults } from "flow/bindings/fancytreeBinding";

export interface IInspectorViewContext {
    viewModel: any;
    myFlow: MyFlow;
}

function InspectorViewModel() {
    let closeFunc: CloseCallback;
    const treeHandle = ko.observable();
    let viewModel: any;
    let vmFlow: MyFlow;
    let componentId: string;
    const details = ko.observable();
    const collator = new Intl.Collator("en");

    function lazyLoad(_event: Event, data: Fancytree.EventData) {
        const node = data.node;

        data.result = function () {
            return getChildren(node, node.key);
        };
    }

    function getValuePath(path: string, propertyName: string, parent: any, propertyValue: any) {
        let newPath = path;
        if (parent.isArray) {
            newPath += "[" + propertyName + "]";
        } else {
            if (path != "") {
                newPath += ".";
            }
            newPath += propertyName;
        }

        if (ko.isObservable(propertyValue)) {
            newPath += "()";
        }

        return newPath;
    }

    function getPabloValuePath(path: string, propertyName: string, parent: any) {
        let newPath = path;
        if (parent.isArray) {
            newPath += "[" + propertyName + "]";
        } else {
            if (path != "") {
                newPath += ".";
            }
            newPath += propertyName;
        }

        return newPath;
    }

    function getValue(value: any): any {
        if (isBaseTypeValue(value)) {
            return value;
        }

        if (ko.isObservable(value)) {
            try {
                return getValue(value());
            } catch (e) {
                return "Throws exception (" + e + ")";
            }
        }

        return "Complex value";
    }

    function getChildren(node: Fancytree.FancytreeNode, parentKey: string) {
        const children: any[] = [];
        const data = node.data;
        const path = data.path;
        const pabloPath = data.pabloPath;

        const context = data.context;

        if (isObservableOrObject(context)) {
            Object.keys(context)
                .sort(collator.compare)
                .forEach(prop => {
                    const key = parentKey + "." + prop;
                    children.push({
                        title: prop,
                        key: key,
                        folder: canContainChildren(context[prop]),
                        lazy: canContainChildren(context[prop]),
                        path: getValuePath(path, prop, data, context[prop]),
                        pabloPath: getPabloValuePath(pabloPath, prop, data),
                        typeText: getTypeDescription(context[prop]),
                        value: getValue(context[prop]),
                        context: ko.unwrap(context[prop]),
                        //handle: { handle: context[prop] },
                        isArray: isArray(context[prop])
                    });
                });
        }

        return children;
    }

    function isArray(value: any) {
        if (value instanceof Array) {
            return true;
        }

        return isObservableArray(value);
    }

    function isObservableArray(value: any) {
        return (ko as any).isObservableArray(value);
    }

    function isObservableOrObject(property: any) {
        return (typeof property == "function" && ko.isObservable(property)) || typeof property == "object";
    }

    function canContainChildren(property: any): boolean {
        if (typeof property == "object") return true;
        if (typeof property == "function" && ko.isObservable(property)) {
            return canContainChildren(property());
        }
        return false;
    }

    function nodeClicked(_event: Event, data: Fancytree.EventData) {
        const node = data.node;
        details({
            title: node.title,
            type: node.data.typeText,
            value: node.data.value,
            //handle: node.data.handle.handle,
            //canBeModified: canBeModified(node.data.handle.handle),
            componentId: componentId,
            path: node.data.path,
            pabloPath: node.data.pabloPath
        });
    }

    //var canBeModified = function(obj) {
    //    return ko.isObservable(obj) && !ko.isComputed(obj) && !isObservableArray(obj);
    //};

    function getViewModelName() {
        if (vmFlow.manifest.id === "flow") {
            return "workspace";
        }

        const vmPath = vmFlow.manifest.viewModelId;
        const index = vmPath.lastIndexOf("/");
        return vmPath.substr(index + 1);
    }

    function isBaseTypeValue(value: any) {
        return (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean" ||
            typeof value === "undefined"
        );
    }

    function getBaseTypeDescription(value: any) {
        return typeof value;
    }

    function getTypeDescription(obj: any): string {
        if (isBaseTypeValue(obj)) {
            return getBaseTypeDescription(obj);
        }

        if (typeof obj === "object") {
            if (obj === null) {
                return "null";
            }

            if (obj instanceof Array) {
                return "Array";
            }

            return "Object";
        }

        if (typeof obj === "function") {
            if (ko.isObservable(obj)) {
                const returnText = ko.isComputed(obj) ? "computed " : "";

                if (isObservableArray(obj)) {
                    return returnText + "observableArray (" + getTypeDescription(obj()) + ")";
                }

                return returnText + "observable (" + getTypeDescription(obj()) + ")";
            }

            return "function";
        }

        throw new Error("Unknown type encountered for: " + obj);
    }

    function activate(viewContext: IInspectorViewContext, closeCallback: CloseCallback) {
        viewModel = viewContext.viewModel;
        vmFlow = viewContext.myFlow;
        componentId = vmFlow.manifest.id;
        closeFunc = closeCallback;
        details(undefined);

        setTimeout(function () {
            treeHandle().create({
                ...fancytreeReadonlyDefaults,
                lazyLoad: lazyLoad,
                source: [
                    {
                        title: getViewModelName(),
                        key: "root",
                        folder: canContainChildren(viewModel),
                        lazy: canContainChildren(viewModel),
                        path: "",
                        pabloPath: "",
                        typeText: getTypeDescription(viewModel),
                        value: getValue(viewModel),
                        context: viewModel,
                        isArray: isArray(viewModel)
                    }
                ],
                activate: nodeClicked
            });
        }, 100);
    }

    function close() {
        closeFunc(false);
    }

    function commands() {
        return [{ text: "OK", click: close }];
    }

    function getDimensions(maxWidth: number, maxHeight: number) {
        return {
            width: maxWidth - 40,
            height: maxHeight - 40
        };
    }

    return {
        activate: activate,
        commands: commands,
        treeHandle: treeHandle,
        details: details,
        getDimensions: getDimensions
    };
}

export default InspectorViewModel;
